import superagent from 'superagent';
import {createAsyncThunk, createSlice, current, unwrapResult} from '@reduxjs/toolkit';
import {normalize, schema} from 'normalizr';

import {getDefaultDecisions} from '../util/getDefaultDecisions';
import {indexConditions} from '../util/indexConditions';
import {spcExpliciteChoiceDecision} from '../util/values';
import {mapReluxDownloads} from '../util/mapReluxDownloads';
import {regionToValueMap} from '../valueMaps/regionToValueMap';

export const fetchModels = createAsyncThunk(
	'productData/models',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/${getState().status.mode}/models/${getState().status.productFamily}`)
			.then((response) => response.body)
			.catch((error) => error)
);


export const fetchProductData = createAsyncThunk(
	'productData/productDataLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/productData/attributes/${options.modelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchConditionalVariantValues = createAsyncThunk(
	'productData/conditionalVariantValuesLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/conditionalvariantvalues/${options.modelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchTranslations = createAsyncThunk(
	'productData/translationsLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/translations/${options.modelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchVariantCodeTypeTemplate = createAsyncThunk(
	'productData/variantCodeTypeTemplateLoading',
	(options, {getState}) =>
		superagent
			.get(`${getState().status.pimUrl}/variant-code-template-data/${options.modelId}/`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchLLMF = createAsyncThunk(
	'productData/LLMFLoading',
	(options) =>
		superagent
			.get(`${options.url}`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchRelux1 = createAsyncThunk(
	'productData/Relux1Loading',
	(options) =>
		superagent
			.get(`${options.url}`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchRelux2 = createAsyncThunk(
	'productData/Relux2Loading',
	(options) =>
		superagent
			.get(`${options.url}`)
			.then((response) => response.body)
			.catch((error) => error)
);
export const fetchAll = createAsyncThunk(
	'productData/allLoading',
	async (options, {getState, dispatch}) => {
		// add the productDataModelId, if different
		console.log('productData fetchAll', options);
		const promises = [
			//dispatch(fetchProductData(options)),
			dispatch(fetchTranslations(options)),
			dispatch(fetchConditionalVariantValues(options)),
			dispatch(fetchVariantCodeTypeTemplate(options))
		];
		const actions = await Promise.all(promises);
		return actions.map(unwrapResult);
	}
);

const productDataSet = {
	productData: [],
	translations: [],
	conditionalVariantValues: {},
	variantCodeTypeTemplate: [],
	originalConditionalVariantValues: [],
	llmf: null,
	relux1: null,
	relux2: null,
	defaults: [],
};
export const productDataSlice = createSlice({
	name: 'productData',
	initialState: {
		status: 'idle',
		models: [],
		modelTranslations: [],
		error: {},
		productDatas: {},
		defaultModelId: null,
		productData: [],
		translations: [],
		conditionalVariantValues: {},
		variantCodeTypeTemplate: [],
		originalConditionalVariantValues: [],
		llmf: null,
		relux1: null,
		relux2: null,
		defaults: [],
	},
	reducers: {
		initDefaults: (state, action) => {
			const region = action.payload.region ?? 'int';
			const modelId = action.payload.modelId;
			if (!Object.hasOwn(state.productDatas, action.payload.modelId)) {
				state.productDatas[action.payload.modelId] = {};
			}
			state.productDatas[modelId].defaults = getDefaultDecisions(state.productDatas[modelId].productData, current(state.productDatas[modelId].originalConditionalVariantValues));
			state.productDatas[modelId].defaults.push(spcExpliciteChoiceDecision('root::CX_Region', regionToValueMap[region]));

			state.defaults = getDefaultDecisions(state.productDatas[modelId].productData, current(state.productDatas[modelId].originalConditionalVariantValues));
			state.defaults.push(spcExpliciteChoiceDecision('root::CX_Region', regionToValueMap[region]));
		},
		// TODO: instanced relux
		clearRelux1: state => {
			state.relux1 = null;
		},
		clearRelux2: state => {
			state.relux2 = null;
		}
	},
	extraReducers: (builder) => {
		/*
		builder.addCase(fetchModels.fulfilled, (state, action) => {
			state.models = action.payload.models;
			state.modelTranslations = normalize(
				action.payload.translations,
				[new schema.Entity('modelTranslations', undefined, {idAttribute: (e) => (`${e.languageTwoLetterCode}.${e.resourceKey.trim()}`)})]
			).entities.modelTranslations;
			state.status = 'idle';
			state.error = {};
		});
		*/

		builder.addCase(fetchProductData.fulfilled, (state, action) => {
			state.productData = action.payload;
			if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
				state.productDatas[action.meta.arg.modelId] = {};
			}
			state.productDatas[action.meta.arg.modelId].productData = action.payload;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchProductData.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchProductData.rejected, (state, action) => {
			state.data = [];
			state.status = 'idle';
			state.error = action.payload;
		});

		builder.addCase(fetchConditionalVariantValues.fulfilled, (state, action) => {
			//console.log('fetchConditionalVariantValues.fulfilled', action.payload, action.meta);
			const conditionalVariantValues = indexConditions(action.payload);

			state.originalConditionalVariantValues = action.payload;
			state.conditionalVariantValues = conditionalVariantValues;

			if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
				state.productDatas[action.meta.arg.modelId] = {};
			}
			state.productDatas[action.meta.arg.modelId].originalConditionalVariantValues = action.payload;
			state.productDatas[action.meta.arg.modelId].conditionalVariantValues = conditionalVariantValues;
			//console.log('conditionalVariantValues', action.meta.arg.modelId, conditionalVariantValues);

			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchConditionalVariantValues.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchConditionalVariantValues.rejected, (state, action) => {
			state.conditionalVariantValues = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchTranslations.fulfilled, (state, action) => {
			const translations = normalize(
				action.payload,
				[new schema.Entity('translations', undefined, {idAttribute: (e) => (`${e.LanguageTwoLetterCode}.${e.Context}.${e.ResourceKey}`)})]
			).entities.translations;

			state.translations = translations;

			if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
				state.productDatas[action.meta.arg.modelId] = {};
			}
			state.productDatas[action.meta.arg.modelId].translations = translations;

			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchTranslations.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchTranslations.rejected, (state, action) => {
			state.translations = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchVariantCodeTypeTemplate.fulfilled, (state, action) => {
			state.variantCodeTypeTemplate = action.payload;
			console.log('fetchVariantCodeTypeTemplate.fulfilled', action.payload, action.meta);
			if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
				state.productDatas[action.meta.arg.modelId] = {};
			}
			state.productDatas[action.meta.arg.modelId].variantCodeTypeTemplate = action.payload;

			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchVariantCodeTypeTemplate.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchVariantCodeTypeTemplate.rejected, (state, action) => {
			state.variantCodeTypeTemplate = [];
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(fetchLLMF.fulfilled, (state, action) => {
			state.llmf = action.payload;

			if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
				state.productDatas[action.meta.arg.modelId] = {};
			}
			state.productDatas[action.meta.arg.modelId].llmf = action.payload;

			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchRelux1.fulfilled, (state, action) => {
			if (action.payload) {
				state.relux1 = mapReluxDownloads(action.payload.data);

				if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
					state.productDatas[action.meta.arg.modelId] = {};
				}
				state.productDatas[action.meta.arg.modelId].relux1 = mapReluxDownloads(action.payload);

				state.status = 'idle';
				state.error = {};
			}
		});
		builder.addCase(fetchRelux2.fulfilled, (state, action) => {
			if (action.payload) {
				state.relux2 = mapReluxDownloads(action.payload.data);

				if (!Object.hasOwn(state.productDatas, action.meta.arg.modelId)) {
					state.productDatas[action.meta.arg.modelId] = {};
				}
				state.productDatas[action.meta.arg.modelId].relux2 = mapReluxDownloads(action.payload);

				state.status = 'idle';
				state.error = {};
			}
		});

		builder.addCase(fetchAll.fulfilled, (state, action) => {
			console.log('fetchAll fulfilled', action);
			const modelId = action.meta.arg.modelId;
			const region = action.meta.arg.region;
			const useAsDefault = action.meta.arg.useAsDefault;

			if (!Object.hasOwn(state.productDatas, modelId)) {
				state.productDatas[modelId] = {};
			}
			const mappedData = {
				productData: {},
				translations: normalize(
					action.payload[0],
					[new schema.Entity('translations', undefined, {idAttribute: (e) => (`${e.LanguageTwoLetterCode}.${e.Context}.${e.ResourceKey}`.toLowerCase())})]
				).entities.translations,
				originalConditionalVariantValues: action.payload[1],
				conditionalVariantValues: indexConditions(action.payload[1]),
				variantCodeTypeTemplate: action.payload[2],
				defaults: null
			};
			Object.assign(state.productDatas[modelId], mappedData);

			//state.productDatas[modelId].defaults = getDefaultDecisions(mappedData.productData, mappedData.originalConditionalVariantValues);
			//state.productDatas[modelId].defaults.push(spcExpliciteChoiceDecision('root::CX_Region', regionToValueMap[region]));

			if (useAsDefault) {
				state.defaultModelId = modelId;
				state.productData = mappedData.productData;
				state.translations = mappedData.translations;
				state.originalConditionalVariantValues = mappedData.originalConditionalVariantValues;
				state.conditionalVariantValues = mappedData.conditionalVariantValues;
				state.variantCodeTypeTemplate = mappedData.variantCodeTypeTemplate;
				state.defaults = null;
			}
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchAll.pending, (state, action) => {
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchAll.rejected, (state, action) => {
			state.status = 'idle';
			state.error = action.payload;
		});
	}
});

export const {initDefaults, clearRelux1, clearRelux2} = productDataSlice.actions;
export default productDataSlice.reducer;
